
import { defineComponent } from "vue";

import Loader from "@/components/Loader.vue";
import CheckBox from "@/components/Checkbox.vue";
import { State } from "@/store";
import { mapState } from "vuex";

const phoneRegex = /^\+?[0-9\s]+$/;

enum FieldNames {
    firstName = 1,
    lastName = 2,
    email = 3,
    phone = 4,
    videoAgreement = "videoAgreement"
}

export default defineComponent({
    name: "PersonalDataForm",
    components: {
        Loader,
        CheckBox
    },
    data() {
        return {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            videoAgreement: false,
            consentChecked: false,
            isPending: false,
            errors: {} as { [key: string]: string },
            fieldNames: FieldNames
        };
    },
    computed: {
        formInvalid() {
             const numericFieldNames = Object.values(FieldNames).filter(
                (v): v is number => typeof v === 'number'
            );

            return this.qrcodeObject.project.shortlinkBurnFields.some(field =>
                numericFieldNames.some(key =>
                field.fieldId === key && this.checkField(key as FieldNames).length > 0
                )
            );
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject
        }),
        firstNameLabel() {
            let label = this.$t("personalDataForm.firstNameLabel");

            if (this.getBurnField(1).isRequired) {
                label += "*";
            }

            return label;
        },
        lastNameLabel() {
            let label = this.$t("personalDataForm.lastNameLabel");

            if (this.getBurnField(2).isRequired) {
                label += "*";
            }
            return label;
        },
        emailLabel() {
            let label = this.$t("personalDataForm.emailLabel");

            if (this.getBurnField(3).isRequired) {
                label += "*";
            }

            return label;
        },
        phoneLabel() {
            let label = this.$t("personalDataForm.phoneLabel");

            if (this.getBurnField(4).isRequired) {
                label += "*";
            }

            return label;
        },
        videoAgreementText() {
            let text = this.qrcodeObject.project.agreementMessage;

            if (this.qrcodeObject?.project?.videoAgreementTypeId === 2) {
                text += "*";
            }

            return text;
        }
    },
    methods: {
        getBurnField(id: number) {
            return this.qrcodeObject?.project?.shortlinkBurnFields.find(
                field => field.fieldId === id
            );
        },
        async submitForm() {
            const videoAgreementField = this.checkField(FieldNames.videoAgreement);

            if (videoAgreementField) {
                this.errors[FieldNames.videoAgreement] = videoAgreementField;
                return;
            }

            this.$store.dispatch("record/setUserData", {
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                phone: this.phone,
                videoAgreement: this.videoAgreement
            });
            this.$router.push("Upload");
        },
        clearFieldError(fieldName: string) {
            delete this.errors[fieldName];
        },
        toggleAgreement(value: boolean) {
            this.videoAgreement = value;
            this.clearFieldError(FieldNames.videoAgreement);
        },
        checkField(fieldName: FieldNames) {
            let result = "";
            let field;
            switch (fieldName) {
                case FieldNames.firstName:
                    field = this.getBurnField(1);
                    if (field && field.isRequired && !this.firstName.length) {
                        result = "fieldRequired";
                    }
                    break;

                case FieldNames.lastName:
                    field = this.getBurnField(2);
                    if (field && field.isRequired && !this.lastName.length) {
                        result = "fieldRequired";
                    }
                    break;

                case FieldNames.email:
                    field = this.getBurnField(3);
                    if (field && field.isRequired && !this.email.length) {
                        result = "fieldRequired";
                    }
                    break;

                case FieldNames.videoAgreement:
                    if (
                        this.qrcodeObject?.project?.videoAgreementTypeId === 2 &&
                        !this.videoAgreement
                    ) {
                        result = "videoAgreement";
                    }
                    break;

                case FieldNames.phone:
                    field = this.getBurnField(4);
                    if (field && field.isRequired && !this.phone.length) {
                        result = "fieldRequired";
                    } else if (
                        !this.getBurnField(4).isRequired &&
                        this.phone.length &&
                        !phoneRegex.test(this.phone)
                    ) {
                        result = "fieldValueIncorrect";
                    }
                    break;
            }
            return result;
        },
        onFieldBlur(fieldName: FieldNames) {
            const errors = this.checkField(fieldName);
            this.errors[fieldName] = errors;
        },
        onEmailInput(ev: Event) {
            const input = ev.target as HTMLInputElement;
            input.value = input.value.toLowerCase();
        }
    }
});
